/**
 * Fields that pack several values: the upgrades and keystate bytes, and the minimap's low-resolution positions in
 * SCORE_BOARD. The arithmetic is 6.2.0's, to the bit.
 */

/** The map's size in map units. */
export const MAP_SIZE = {
  WIDTH: 32768,
  HEIGHT: 16384,
} as const;

/** The four movement keys a keystate carries. */
export interface Keystate {
  UP: boolean;
  DOWN: boolean;
  LEFT: boolean;
  RIGHT: boolean;
}

/** The upgrades byte: the speed upgrades in the low three bits, then a shield bit and an inferno bit. */
export function encodeUpgrades(speed: number, shield: number, inferno: number): number {
  return (speed & 7) | (shield << 3) | (inferno << 4);
}

export function decodeUpgrades(upgrades: number): { speed: number; shield: number; inferno: number } {
  return {
    speed: upgrades & 7,
    shield: (upgrades & 8) >> 3,
    inferno: (upgrades & 16) >> 4,
  };
}

/** The keystate byte: up, down, left, right, boost, strafe, stealthed, flag speed, from the low bit. Up masks down. */
export function encodeKeystate(
  keystate: Keystate,
  boost: boolean,
  strafe: boolean,
  stealthed: boolean,
  flagspeed: boolean,
): number {
  const down = keystate.DOWN === true ? 1 : 0;

  return (
    0 |
    ((keystate.UP === true ? 1 : 0) << 0) |
    ((keystate.UP === true ? 0 : down) << 1) |
    ((keystate.LEFT === true ? 1 : 0) << 2) |
    ((keystate.RIGHT === true ? 1 : 0) << 3) |
    ((boost === true ? 1 : 0) << 4) |
    ((strafe === true ? 1 : 0) << 5) |
    ((stealthed === true ? 1 : 0) << 6) |
    ((flagspeed === true ? 1 : 0) << 7)
  );
}

export function decodeKeystate(keystate: number): {
  keystate: Keystate;
  boost: boolean;
  strafe: boolean;
  stealthed: boolean;
  flagspeed: boolean;
} {
  return {
    keystate: {
      UP: (keystate & (1 << 0)) === 1 << 0,
      DOWN: (keystate & (1 << 1)) === 1 << 1,
      LEFT: (keystate & (1 << 2)) === 1 << 2,
      RIGHT: (keystate & (1 << 3)) === 1 << 3,
    },
    boost: (keystate & (1 << 4)) === 1 << 4,
    strafe: (keystate & (1 << 5)) === 1 << 5,
    stealthed: (keystate & (1 << 6)) === 1 << 6,
    flagspeed: (keystate & (1 << 7)) === 1 << 7,
  };
}

function clamp(value: number, lower: number, upper: number): number {
  if (value <= lower) {
    return lower;
  }

  if (value >= upper) {
    return upper;
  }

  return value;
}

/** A SCORE_BOARD ranking's low-resolution position as map coordinates. */
export function decodeMinimapCoords(x: number, y: number): { x: number; y: number } {
  return {
    x: 128 * x - MAP_SIZE.WIDTH / 2 + 64,
    y: clamp(128 * y - MAP_SIZE.HEIGHT, -MAP_SIZE.HEIGHT / 2, MAP_SIZE.HEIGHT / 2) + 64,
  };
}
